// @flow
import * as React from 'react';
import queryString from 'query-string';
import deepEqual from 'react-fast-compare';
import debounce from 'lodash.debounce';
import type { Subsite } from 'api/website';
import { withRouter } from 'react-router';
import type { Location, RouterHistory } from 'react-router';
import { connect } from 'react-redux';
import { objectEmpty } from 'utils/functional';
import { trackEvent } from 'utils/analytics';
import getPostSearchQueryString from 'utils/search/getPostSearchQueryString';
import PostSearchResults from 'components/PostSearchResults';
import { setScrollRestorationManual } from 'utils/browser';
import getPostSearchTrackingData from 'utils/getPostSearchTrackingData';
import { PostSearchLoadingSection as SearchLoadingSection } from 'components/SearchLoadingSection';
import type { State as PostSearch } from '../redux/main/reducers/postSearch';
import { results as resultsFn } from '../redux/main/reducers/postSearch';
import { clearSearchLocation } from '../redux/main/actions/currentUser';
import {
  initializeSearch,
  setSearchQuery,
  setPageIndex,
  search
} from '../redux/main/actions/postSearch';

const TRACK_DISPLAYED_PROGRAM_WAIT = 500;

const trackDisplayedPosts = (
  hits: Array<Object>,
  searchTrackingData: Object,
  subsite: { id: string, name: string, ... }
) => {
  const articleIds = hits ? hits.map(h => h.objectID) : [];
  const rankingInfo = hits
    ? hits.map(({ _rankingInfo, objectID }) => ({
        rankingInfo: _rankingInfo,
        objectID
      }))
    : [];

  trackEvent('Displayed CMS Articles', {
    article_ids: articleIds,
    ranking_info: rankingInfo,
    subsite_id: subsite.id,
    subsite_name: subsite.name,
    page_type: 'Searches for Articles',
    ...searchTrackingData
  });
};

const debouncedTrackDisplayedPosts = debounce(
  trackDisplayedPosts,
  TRACK_DISPLAYED_PROGRAM_WAIT
);

type OwnProps = {|
  children: React.Node,
  content: {
    locale: string,
    subsite: Subsite
  },
  history: RouterHistory,
  location: Location,
  modalShown: boolean,
  parentSubsite: ?Subsite
|};

type Props = {
  ...$Exact<OwnProps>,
  getSearchTrackingData: () => Object,
  postSearch: PostSearch,
  initializeSearch: Function,
  search: Function,
  setSearchQuery: typeof setSearchQuery,
  setPageIndex: Function,
  clearSearchLocation: typeof clearSearchLocation
};

class PostSearchContainer extends React.Component<Props> {
  search = () => {
    const { id: subsiteId, includedSubsites = [] } = this.props.content.subsite;
    this.props.search(
      subsiteId,
      includedSubsites.map(subsiteMini => subsiteMini.id),
      this.props.content.locale
    );
  };

  componentDidMount() {
    const { initialized, query } = this.props.postSearch;
    const { pq: queryParam } = queryString.parse(this.props.location.search);

    if (initialized) {
      let performSearch = true;
      if (queryParam !== undefined && queryParam !== query) {
        this.props.setSearchQuery(queryParam, false);
        performSearch = false;
      }

      if (performSearch) {
        this.search();
      }
    }
    setScrollRestorationManual();
  }

  componentDidUpdate(prevProps: Props) {
    const prevSearch = prevProps.postSearch;
    const { location, postSearch } = this.props;
    const {
      facetsThatArentLoaded,
      filters,
      initialSearchStarted,
      searchFacets,
      query
    } = postSearch;
    const filtersHaveChanged =
      !deepEqual(prevSearch.filters, filters) ||
      !deepEqual(prevSearch.searchFacets, searchFacets);

    if (filtersHaveChanged) {
      const postSearchQueryString = getPostSearchQueryString(
        filters,
        searchFacets,
        query,
        location.search
      );
      this.props.history.replace({
        pathname: this.props.location.pathname,
        search: postSearchQueryString,
        state: this.props.location.state
      });
    }

    // This is needed for back button behavior, may be worth revisiting
    const queryHasChanged =
      queryString.parse(this.props.location.search).pq !==
      queryString.parse(prevProps.location.search).pq;

    // All asynchronously loaded facets have been loaded
    const facetsAreLoaded = facetsThatArentLoaded.length === 0;

    const resultsArentLoaded =
      prevSearch.searchId !== postSearch.searchId ||
      (prevSearch.pageIndex !== postSearch.pageIndex &&
        !postSearch.resultsByPage[postSearch.pageIndex.toString()]);

    if (queryHasChanged) {
      this.props.setSearchQuery(
        queryString.parse(this.props.location.search).pq,
        false
      );
      this.search();
    } else if (
      facetsAreLoaded &&
      (!initialSearchStarted || resultsArentLoaded)
    ) {
      this.search();
    } else {
      const { page: prevPageParam } = queryString.parse(
        prevProps.location.search
      );
      const { page: pageParam } = queryString.parse(location.search);

      const { pageIndex: prevPageIndex } = prevSearch;
      const { pageIndex, resultsByPage } = postSearch;

      const pageIndexParam = (parseInt(pageParam, 10) || 1) - 1;

      if (
        prevPageIndex === pageIndex &&
        prevPageParam !== pageParam &&
        pageIndexParam !== pageIndex
      ) {
        this.props.setPageIndex(pageIndexParam, true);
      }

      if (objectEmpty(resultsByPage)) {
        return;
      }

      const prevResults = resultsFn(prevSearch);
      const results = resultsFn(postSearch);
      if (
        !this.props.modalShown &&
        results &&
        (!prevResults || prevResults.timestamp !== results.timestamp)
      ) {
        debouncedTrackDisplayedPosts(
          results.hits,
          this.props.getSearchTrackingData(),
          this.props.content.subsite
        );
      }
    }
  }

  render() {
    const scrollToId =
      this.props.location.state && this.props.location.state.scrollToId;
    return resultsFn(this.props.postSearch) ? (
      <PostSearchResults
        {...this.props}
        search={this.props.postSearch}
        homePageUrl={this.props.content.subsite.homePage.url}
        scrollToId={scrollToId}
        subsite={this.props.content.subsite}
        parentSubsite={this.props.parentSubsite}
        locale={this.props.content.locale}
      />
    ) : (
      <SearchLoadingSection />
    );
  }
}

const mapStateToProps = state => {
  const {
    postSearch,
    modal: { type }
  } = state;
  return {
    postSearch,
    getSearchTrackingData: () => getPostSearchTrackingData(state),
    modalShown: !!type
  };
};

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // $FlowFixMe
    {
      clearSearchLocation,
      initializeSearch,
      search,
      setPageIndex,
      setSearchQuery
    }
  )(PostSearchContainer)
);
